import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { GetAnswerRequest } from '../../../../../../backend/src/core/openai/openai.gate';
import { SelectDeepgramComponent } from '../../../basic/deepgram/select-deepgram/select-deepgram.component';
import { CompanyService } from '../../../core/company/company.service';
import { OpenaiModel } from '../../../core/openai/openai';
import { OpenaiService } from '../../../core/openai/openai.service';
import { PersonalityService } from '../../../core/personality/personality.service';
import {
  changeResumeNameFunctions,
  changeResumeNamePrompt,
} from '../../../core/prompt/resume/change-resume-name.prompt';
import { Resume, ResumeType } from '../../../core/resume/resume';
import { ResumeService } from '../../../core/resume/resume.service';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { EditCustomerPortraitComponent } from '../../portrait/edit-customer-portrait/edit-customer-portrait.component';

@Component({
  selector: 'app-edit-resume',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzModalModule,
    NzButtonModule,
    NzInputModule,
    NzPopconfirmModule,
    NzTagModule,
    NzCollapseModule,
    NzTagModule,
    MatTooltip,
    NzPopoverModule,
    NzTabsModule,
    NzDividerModule,
    NzSelectModule,
    NzRadioModule,

    SelectDeepgramComponent,
    EditCustomerPortraitComponent,
  ],
  templateUrl: './edit-resume.component.html',
  styleUrl: './edit-resume.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditResumeComponent {
  @Input({ required: true }) resume!: Resume;

  @Input() isEdit: boolean = false;

  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  constructor(
    private companyService: CompanyService,
    private resumeService: ResumeService,
    private messageService: NzMessageService,
    private personalityService: PersonalityService,
    private sessionTemplateService: SessionTemplateService,
    private openaiService: OpenaiService
  ) {}

  ResumeType = ResumeType;

  isChangeNameVisible = false;

  companyList = toSignal(this.companyService.itemList$(), { initialValue: [] });
  personalityList = toSignal(this.personalityService.itemList$(), { initialValue: [] });

  selectablePersonalityList = computed(() => {
    const personalityList = this.personalityList();

    return personalityList.map((personality) => {
      return {
        isSelected: this.resume.subData?.content?.personalityIdList?.includes(personality.id),
        value: personality,
      };
    });
  });

  resumeNewFirstName = '';
  resumeNewLastName = '';

  isChangeNameLoading = signal<boolean>(false);

  onChangeNameClick() {
    this.isChangeNameLoading.set(true);

    const content = changeResumeNamePrompt({
      oldName: {
        firstName: this.resume.subData.content!.firstName!,
        lastName: this.resume.subData.content!.lastName!,
      },
      newName: {
        firstName: this.resumeNewFirstName,
        lastName: this.resumeNewLastName,
      },
      resume: this.resume,
    });
    const request: GetAnswerRequest = {
      messageList: [
        {
          role: 'user',
          content: content,
        },
      ],
      config: {
        openaiModel: OpenaiModel.gpt4,
        functions: changeResumeNameFunctions({
          oldName: {
            firstName: this.resume.subData.content!.firstName!,
            lastName: this.resume.subData.content!.lastName!,
          },
          newName: {
            firstName: this.resumeNewFirstName,
            lastName: this.resumeNewLastName,
          },
          resume: this.resume,
        }),
      },
    };
    return this.openaiService
      .getAnswer$(request)
      .pipe(take(1))
      .subscribe((result: any) => {
        this.resume.subData.content = result;

        this.resume.subData.content!.firstName = this.resumeNewFirstName;
        this.resume.subData.content!.lastName = this.resumeNewLastName;

        this.resumeService
          .updateResume$(this.resume)
          .pipe(take(1))
          .subscribe(() => {
            this.isChangeNameLoading.set(false);
            this.isChangeNameVisible = false;
          });
      });
  }

  onCompanyChange(companyId: string) {
    const company = this.companyList().find((c) => c.id === companyId);
    this.resume.subData.content!.mostRecentCompany = company?.title;
  }

  onEditImageClick() {}

  onAddPainPointClick() {
    this.resume.subData.content!.painPoints = [...(this.resume.subData.content!.painPoints || []), ''];
  }

  onDeletePainPointClick(i: number) {
    this.resume.subData.content!.painPoints!.splice(i, 1);
  }

  onPersonalitySelected() {
    this.resume!.subData.content!.personalityIdList = this.selectablePersonalityList()
      .filter((c) => c.isSelected)
      .map((c) => c.value.id);
  }

  onIsVisibleChange(isVisible: boolean) {
    this.isVisibleChange.emit(isVisible);
  }

  onDeleteClick() {
    this.sessionTemplateService
      .itemListByResumeId$(this.resume!.id)
      .pipe(take(1))
      .subscribe((data) => {
        if (data.length === 0) {
          this.isVisible = false;
          this.isVisibleChange.emit(this.isVisible);
          this.resumeService
            .deleteResumeList$([this.resume!])
            .pipe(take(1))
            .subscribe(() => {});
        } else {
          this.messageService.error(
            `Please delete ${data.length} related scenarios to proceed with deleting this customer.`,
            { nzDuration: 10000 }
          );
        }
      });
  }

  isEditCustomerPortraitVisible = false;
  onEditPortraitClick() {
    this.isEditCustomerPortraitVisible = true;
  }

  onCancelClick() {
    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);
  }

  onSubmitClick() {
    if (this.isEdit) {
      this.resumeService
        .updateResume$(this.resume)
        .pipe(take(1))
        .subscribe(() => {
          this.isVisible = false;
          this.isVisibleChange.emit(this.isVisible);
        });
    } else {
      this.resumeService
        .addResume$(this.resume.subData, this.resume.type)
        .pipe(take(1))
        .subscribe(() => {
          this.isVisible = false;
          this.isVisibleChange.emit(this.isVisible);
        });
    }
  }
}
